import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { Component, Input, SimpleChanges, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Mediaclip, MediaclipSubtitle } from '../../../interfaces/mediaclip';
import { BbTableService } from '../../../services/bb-table.service';
import { BbFile, UploadService } from '../../../services/upload.service';
import { SapiService } from '../../../services/sapi.service';
import { PublicationService } from '../../../services/publication.service';
import { Publication } from '../../../interfaces/publication';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'app/services/authentication.service';
import { User } from 'app/interfaces/user';
import { WebSocketsService } from 'app/services/web-sockets.service';
import { DialogDefinition, OverlayService } from 'app/services/overlay.service';
import { ModalRouteService } from 'app/services/modal-route.service';
import { SubtitleService } from 'app/services/subtitle.service';

export interface Language {
	id?: number;
	isocode?: string;
	name?: string;
	languagename?: string;
	uri?: string;
	originalfilename?: string;
	isdefault?: boolean;
}

const MAX_TABLE_ROWS = 10;

@Component({
	selector: 'mediaclip-details-languages',
	templateUrl: './mediaclip-details-languages.component.html',
	styleUrls: ['./mediaclip-details-languages.component.scss']
})

export class MediaclipDetailsLanguagesComponent implements OnInit, OnChanges {
	@Input() mediaclip: Mediaclip;
	@Input() playerApi: BBPlayerAPI;
	@Input() form: NgForm;
	@Input() isActive: boolean;
	@Output() reloadMediaclip: EventEmitter<boolean>;

	// TODO: Implement rowsContainLiveClips
	rowsContainLiveClips: boolean;

	subtitles: MediaclipSubtitle[];
	audiotracks: Language[];
	subtitleRows: any[];
	audiotrackRows: any[];

	languages: Language[];
	languageOptions: { value: string; label: string }[] = [];

	subtitlesPage: number;
	audiotracksPage: number;

	subscriptions: Subscription;
	defaultMediaAssetPath: string;

	subtitlesAdded: string[];
	audiotracksAdded: string[];

	currentTranscript: any;
	generatingTranscript: boolean;
	useTranscriptionService: boolean;
	isPrivileged: boolean;
	transcriptionLanguage = '';
	transcriptionLanguageOptions = [
		{ label: 'Arabic', value: 'ar-ar' },
		{ label: 'Danish (BETA)', value: 'da-dk' },
		{ label: 'Dutch', value: 'nl-nl' },
		{ label: 'English', value: 'en-us' },
		{ label: 'Filipino', value: 'tl-ph' },
		{ label: 'Finnish (BETA)', value: 'fi-fi' },
		{ label: 'Flemish', value: 'nl-be' },
		{ label: 'French', value: 'fr-fr' },
		{ label: 'German', value: 'de-de' },
		{ label: 'Italian', value: 'it-it' },
		{ label: 'Norwegian', value: 'nb-no' },
		{ label: 'Spanish', value: 'es-es' },
		{ label: 'Swedish', value: 'sv-se' }
	];

	rowData: any;
	uploadedSubtitle: string;

	constructor(
		private bbTableService: BbTableService,
		private sapiService: SapiService,
		private publicationService: PublicationService,
		private translate: TranslateService,
		private authenticationService: AuthenticationService,
		private uploadService: UploadService,
		private webSocketsService: WebSocketsService,
		private modalRouteService: ModalRouteService,
		private overlayService: OverlayService,
		private subtitleService: SubtitleService
	) {

		this.subtitlesPage = 1;
		this.audiotracksPage = 0;
		this.bbTableService
			.setConfigurable(false)
			.setLimit(10);

		this.subtitles = new Array<MediaclipSubtitle>();
		this.audiotracks = new Array<Language>();
		this.subscriptions = new Subscription();
		this.languages = new Array<Language>();
		this.reloadMediaclip = new EventEmitter<boolean>();
		this.useTranscriptionService = false;

		this.authenticationService.getUser().subscribe((user: User) => {
			if (user && user.isPrivileged) {
				this.isPrivileged = true;
			}
		});

		this.subscriptions.add(this.publicationService.getData().subscribe((publicationData: Publication) => {
			if (publicationData) {
				this.defaultMediaAssetPath = publicationData.defaultMediaAssetPath;
				if (this.publicationService.hasPublicationField('useTranscriptionService') || this.isPrivileged) {
					this.useTranscriptionService = true;
				}
			}
		}));

		this.subscriptions.add(this.webSocketsService.subscribeToChannel(`transcriptionJob`)
			.subscribe((websocketEvent: any) => {
				this.checkWebsocketEvent(websocketEvent);
			}));

		this.filterSubtitleUploads = this.filterSubtitleUploads.bind(this);
		this.filterAudiotrackUploads = this.filterAudiotrackUploads.bind(this);

		this.subtitlesAdded = [];
		this.audiotracksAdded = [];

		this.sapiService.getUrl('/language').subscribe((languageData: any) => {
			if (languageData && languageData.items && languageData.items.length) {
				this.languages = languageData.items;
				this.languageOptions = this.languages.map((language: any) => ({
					value: language.isocode,
					label: this.translate.instant('LANGUAGE_' + language.isocode.toUpperCase())
				}));
				this.setTranscriptionDefaultLanguage();
			}
		});

		this.uploadService.completedObservable.subscribe((bbFile: BbFile) => {
			let subtitle: boolean;

			let identifierResults = this.subtitlesAdded.filter((uploadIdentifier: string) => bbFile.uploadIdentifier === uploadIdentifier);

			if (identifierResults && identifierResults.length > 0) {
				subtitle = true;
				this.removeItemFromArray(bbFile.uploadIdentifier, this.subtitlesAdded);
			} else {
				subtitle = false;
				identifierResults = this.audiotracksAdded.filter((uploadIdentifier: string) => bbFile.uploadIdentifier === uploadIdentifier);

				if (!identifierResults || identifierResults.length === 0) {
					return;
				} else {
					this.removeItemFromArray(bbFile.uploadIdentifier, this.audiotracksAdded);
				}
			}

			const newLanguage: any = {
				status: 'published',
				mediaclipid: this.mediaclip.id,
				uploadIdentifier: bbFile.uploadIdentifier,
				originalfilename: bbFile.name,
			};


			if (subtitle) {
				this.parseSrtAndOpenEdit(bbFile.file);
			} else {
				// audio
				const nextLanguage = this.getNextAvailableLanguage(this.audiotracks);
				newLanguage.isocode = nextLanguage.isocode;
				newLanguage.languageid = nextLanguage.id;
				if (!this.mediaclip.audiotracks) {
					this.mediaclip.audiotracks = [];
				}
				newLanguage.name = nextLanguage.name;
				this.audiotrackRows.push({ id: 'new', quickEditing: true, data: newLanguage });
			}
		});

		// new or edited subtitles from modal
		this.subscriptions.add(this.subtitleService.subtitleSubject.subscribe((result) => {
			if (!this.mediaclip.subtitles) {
				this.mediaclip.subtitles = [];
			}

			const index = this.mediaclip.subtitles.findIndex((sub => sub.id === result.id));
			if (index > -1) {
				this.mediaclip.subtitles[index] = result;
			} else {
				this.mediaclip.subtitles.push(result);
			}

			this.subtitlePageChanged(1);
			setTimeout(() => {
				this.loadPlayerWithoutCache();
			}, 500);
		}));
	}

	ngOnInit() {
		this.subtitlePageChanged(1);
		this.audiotracksPageChanged(1);
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.mediaclip) {
			this.transformSubtitleAudioData();
		}
	}

	getTranscript() {
		this.sapiService.get('mediaclip', this.mediaclip.id, 'transcriptionJob').subscribe((result: any) => {
			if (result && result.status) {
				if (result.status === 'running') {
					this.generatingTranscript = true;
					return;
				}
				const config = JSON.parse(result.config);
				this.currentTranscript = {
					status: result.status,
					isocode: config.isocode,
					errorMessag: result.status === 'error' && result.errorMessage ? result.errorMessage : ''
				};
			}
		});
	}

	generateTranscript() {
		const transcription = {
			mediaclipid: this.mediaclip.id,
			isocode: this.transcriptionLanguage
		};
		this.currentTranscript = undefined;
		this.generatingTranscript = true;
		this.sapiService.putUrl('/transcriptionjob', transcription).subscribe((result: any) => {
		}, (e) => {
			this.generatingTranscript = false;
			if (e && e.error && e.error.messages && e.error.messages[0].userFriendlyLabel && e.error.messages[0].userFriendlyLabel) {
				const label = e.error.messages[0].userFriendlyLabel;
				const msg = this.translate.instant(label);
				this.overlayService.createToast(msg, 'info-circle');
			} else {
				const msg = this.translate.instant('TRANSCRIPTION_ERROR');
				this.overlayService.createToast(msg, 'warning');
			}
		});
	}

	addSubtitle(): void {
		this.openEditModal('new');
	}

	editSubtitle(id: string): void {
		this.openEditModal(id);
	}

	openEditModal(id: string): void {
		this.modalRouteService.open('subtitle', id, null, this.mediaclip.id);
	}

	deleteSubtitle(id: string, language: string): void {
		const translation = this.translate.instant('SUBTITLE');

		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', { entity: translation, id: language }),
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: translation }),
			onConfirm: () => {
				const returnObservable: Observable<any> = this.sapiService.delete('subtitle', id);

				returnObservable.subscribe(() => {
					this.overlayService.createToast(
						this.translate.instant('ENTITY_DELETED', { id: language, entity: translation }), 'trash'
					);
					if (this.mediaclip.subtitles?.length) {
						this.mediaclip.subtitles = this.mediaclip.subtitles.filter(sub => sub.id !== id);
					} else {
						this.mediaclip.subtitles = [];
					}
					this.subtitlePageChanged(1);
					this.loadPlayerWithoutCache();
				});

				return returnObservable;
			}
		};
		this.overlayService.createDialog(dialog);
	}

	parseSrtAndOpenEdit(file: any) {
		const reader = new FileReader();

		reader.onload = (e: any) => {
			const data = e.target.result;
			if (data) {
				this.uploadedSubtitle = data;

				this.addSubtitle();
				this.subtitleService.setUploadedSrt(this.uploadedSubtitle);

				if (!this.mediaclip.subtitles) {
					this.mediaclip.subtitles = [];
				}
			}
		};

		reader.onerror = (ex) => {
			console.warn('Error parsing example XML:', ex);
		};

		reader.readAsText(file);
	}

	subtitleFileCreated(bbFile: BbFile) {
		if (bbFile) {
			this.subtitlesAdded.push(bbFile.uploadIdentifier);
		}
	}

	audiotrackFileCreated(bbFile: BbFile) {
		if (bbFile) {
			this.audiotracksAdded.push(bbFile.uploadIdentifier);
		}
	}

	removeItemFromArray(item: string, array: string[]) {
		const index = array.indexOf(item);
		if (index > -1) {
			array.splice(index, 1);
		}
	}

	onItemRemoved(items: any[], index: number) {
		items.splice(index, 1);
	}

	showPreview(id: string) {
		if (this.playerApi) {
			this.playerApi.play();
			this.playerApi.setSubtitle(id);
		}
	}

	filterSubtitleUploads(file: BbFile): boolean {
		return file.name.endsWith('.srt') || file.name.endsWith('.ttxt');
	}

	filterAudiotrackUploads(file: BbFile): boolean {
		return file.type.startsWith('audio/') || file.type.startsWith('video/');
	}

	getNextAvailableLanguage(usedLanguages: any[]): Language {
		if (this.languages) {
			const usedLanguageIds = usedLanguages.map((language: any) => Number(language.languageid));
			const usedQuickeditLanguageIds = this.subtitleRows.map((language: any) => Number(language.data.languageid));
			let nextLanguage = null;
			for (let i = 0; i < this.languages.length; i++) {
				const language = this.languages[i];
				if (usedLanguageIds.indexOf(language.id) !== -1 || usedQuickeditLanguageIds.indexOf(language.id) !== -1) {
					continue;
				}
				nextLanguage = language;
				break;
			}
			if (!nextLanguage) {
				nextLanguage = this.languages[0];
			}
			return nextLanguage;
		}
		return null;
	}

	downloadSubtitle(id) {
		this.sapiService.download('subtitle', id, 'download', this.getSubtitleFilename(id)).subscribe();
	}

	downloadAudiotrack(id: number): string {
		const result: Language[] = this.audiotracks.filter((audiotrack: any) => id === audiotrack.id);
		let audiotrackFound: Language;
		if (result.length > 0) {
			audiotrackFound = result[0];
		}
		if (audiotrackFound && this.defaultMediaAssetPath && audiotrackFound.uri && audiotrackFound.uri.length > 0) {
			return this.defaultMediaAssetPath + audiotrackFound.uri;
		}
		return null;
	}

	subtitlePageChanged(pageNumber: number) {
		if (this.mediaclip && this.mediaclip.subtitles) {
			const offset = (pageNumber - 1) * MAX_TABLE_ROWS;
			const end = offset + MAX_TABLE_ROWS;
			this.subtitles = this.mediaclip.subtitles.slice(offset, end);
			this.subtitleRows = this.subtitles.map((subtitle) => ({
				id: subtitle.id,
				data: subtitle
			}));
		} else {
			this.subtitleRows = [];
		}
		this.subtitlesPage = pageNumber;
	}

	getSubtitleFilename(subtitleId): string {
		const subtitleData = this.mediaclip.subtitles?.find((subtitle) => subtitle.id === subtitleId);
		if (!subtitleData) {
			return null;
		}
		return `${this.mediaclip.title || this.mediaclip.originalfilename || this.mediaclip.id} - ${subtitleData.languagename}`;
	}

	audiotracksPageChanged(pageNumber: number) {
		if (this.mediaclip && this.mediaclip.audiotracks) {
			const offset = (pageNumber - 1) * MAX_TABLE_ROWS;
			const end = offset + MAX_TABLE_ROWS;
			this.audiotracks = this.mediaclip.audiotracks.slice(offset, end);
			this.audiotrackRows = this.audiotracks.map((audiotrack) => ({
				id: audiotrack.id,
				data: audiotrack
			}));
			// Also audiotracks with status 'deleted' are retrieved, so filter those
			this.audiotrackRows = this.audiotrackRows.filter(row => row.data.status !== 'deleted');
		} else {
			this.audiotrackRows = [];
		}
	}

	trackBy(index: number, item: any) {
		if (item.id !== 'new') {
			return item.id || index;
		}
		return index;
	}

	// short term solution to load player without cached clip data (new subtitle data)
	private loadPlayerWithoutCache(): void {
		if (!this.playerApi) {
			return;
		}

		this.playerApi.load({ clipId: this.mediaclip.id + '?cachebuster=' + new Date(), autoPlay: false });
	}

	private transformSubtitleAudioData(): void {
		this.subtitles = [];
		this.audiotracks = [];
		if (this.mediaclip.subtitles) {
			this.mediaclip.subtitles.forEach((subtitle: any) => {
				if (subtitle && subtitle.languagename.startsWith('Automated transcript') && subtitle.isocode) {
					subtitle.languagename = this.translate.instant('LANGUAGE_' + subtitle.isocode.toUpperCase()) + ' (auto)';
				}
			});
		}
		this.subtitlePageChanged(1);
		this.audiotracksPageChanged(1);
		if (this.useTranscriptionService) {
			this.getTranscript();
		}
	}

	private checkWebsocketEvent(websocketEvent: any): void {
		if (!websocketEvent || !websocketEvent.ev || !websocketEvent.payload || websocketEvent.payload.mediaclipid !== this.mediaclip.id) {
			return;
		}

		const ev = websocketEvent.ev;
		const payload = websocketEvent.payload;

		if (ev === 'error') {
			const msg = this.translate.instant('TRANSCRIPTION_ERROR');
			this.overlayService.createToast(msg, 'warning');
			this.generatingTranscript = false;
			this.currentTranscript = {
				status: 'error',
				errorMessage: payload.errorMessage
			};
			return;
		}

		if (ev === 'finish') {
			this.generatingTranscript = false;
			// add data to mediaclip.subtitle + refresh subtitle table
			if (!this.mediaclip.subtitles) {
				this.mediaclip.subtitles = [];
			}
			this.mediaclip.subtitles.push({
				default: 'false',
				id: payload.subtitleid,
				isocode: payload.isocode,
				languageid: payload.languageid,
				languagename: payload.languagename,
				name: '',
				status: 'published'
			});

			// refresh player to show new subtitle
			this.loadPlayerWithoutCache();

			// update this.languages if new (auto) language
			const availableLanguage = this.languages.find(lang => lang.isocode === payload.isocode);
			if (!availableLanguage) {
				this.sapiService.getUrl('/language').subscribe((languageData: any) => {
					if (languageData && languageData.items && languageData.items.length) {
						this.languages = languageData.items;
					}
				});
			}
			this.transformSubtitleAudioData();
		}
	}

	private setTranscriptionDefaultLanguage(): void {
		const defaultLanguage = this.languages.find((language: Language) => language.isdefault === true);
		if (!defaultLanguage || !defaultLanguage.isocode) {
			return;
		}

		const transcriptionLanguage = this.transcriptionLanguageOptions.find(tLanguage =>
			// format DB languages: ['nl']. Format scriptix: ['nl_nl']
			tLanguage.value.includes(defaultLanguage.isocode)
		);

		if (transcriptionLanguage) {
			this.transcriptionLanguage = transcriptionLanguage.value;
		}
	}
}
